<script setup lang="ts">
/**
 * Shows the privacy banner and settings dialog before and after sign-in.
 * Keeps switch edits local until the visitor saves them to the privacy store.
 */
import { nextTick, onMounted, ref, watch } from "vue"
import { usePrivacyStore } from "@/stores/privacyStore"
import { createPrivacyChoices, trackingPurposes } from "@/types/privacyChoices"
import Button from "@/components/ui/Button.vue"
import ArkBaseSwitch from "@/components/ui/ArkBaseSwitch.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

const privacyPolicyUrl = "https://www.landerz.ca/privacypolicy"

const privacy = usePrivacyStore()
const dialog = ref<HTMLDialogElement>()
const draft = ref(createPrivacyChoices())

async function updateDialog(): Promise<void> {
	await nextTick()
	if (privacy.isSettingsOpen) {
		draft.value = { ...privacy.choices }
		if (!dialog.value?.open) dialog.value?.showModal()
	} else {
		dialog.value?.close()
	}
}

watch(() => privacy.isSettingsOpen, updateDialog)
onMounted(updateDialog)

function closeSettings(): void {
	privacy.isSettingsOpen = false
}
</script>

<template>
	<section v-if="privacy.showBanner" class="privacy-banner" role="region" :aria-label="$t('privacy.title')">
		<h3>{{ $t("privacy.title") }}</h3>
		<p>{{ $t("privacy.introduction") }}</p>
		<p v-if="privacy.storageError" class="error" role="alert">{{ $t("privacy.storageError") }}</p>
		<div class="actions">
			<div class="links">
				<a :href="privacyPolicyUrl" class="policy" target="_blank" rel="noopener noreferrer">
					{{ $t("privacy.policy") }}
				</a>
				<Button variant="ghost" size="s" class="settings" @click="privacy.openSettings()">
					{{ $t("privacy.changeSettings") }}
				</Button>
			</div>
			<div class="choices">
				<Button variant="secondary" size="s" @click="privacy.saveChoices(createPrivacyChoices())">
					{{ $t("privacy.decline") }}
				</Button>
				<Button variant="primary" size="s" @click="privacy.saveChoices(createPrivacyChoices(true))">
					{{ $t("privacy.acceptAll") }}
				</Button>
			</div>
		</div>
	</section>

	<dialog
		ref="dialog"
		class="privacy-dialog"
		aria-labelledby="privacy-title"
		aria-describedby="privacy-description"
		@cancel="closeSettings"
		@close="closeSettings"
	>
		<div class="heading">
			<h3 id="privacy-title">{{ $t("privacy.settings") }}</h3>
			<Button variant="ghost" size="xs" class="close" :aria-label="$t('privacy.close')" @click="closeSettings">
				<AppIcon :name="IconName.CloseSmall" :size="16" :stroke-width="1.2" />
			</Button>
		</div>
		<p id="privacy-description">{{ $t("privacy.introduction") }}</p>
		<form @submit.prevent="privacy.saveChoices(draft)">
			<ul class="purposes">
				<li v-for="purpose in trackingPurposes" :key="purpose">
					<div class="copy">
						<span class="name">{{ $t(`privacy.purposes.${purpose}.title`) }}</span>
						<p>{{ $t(`privacy.purposes.${purpose}.description`) }}</p>
					</div>
					<ArkBaseSwitch
						:checked="draft[purpose]"
						:label="$t(`privacy.purposes.${purpose}.title`)"
						@change="checked => (draft[purpose] = checked)"
					/>
				</li>
			</ul>
			<AccordionRoot v-if="privacy.isSettingsOpen" :persistent="false">
				<AccordionItem value="data-recipients" :title="$t('privacy.recipients.title')" ghost>
					<div class="recipients">
						<p>{{ $t("privacy.recipients.introduction") }}</p>
						<dl>
							<dt>{{ $t("privacy.purposes.usageStatistics.title") }}</dt>
							<dd>{{ $t("privacy.recipients.usageStatistics") }}</dd>
							<dt>{{ $t("privacy.purposes.sessionRecording.title") }}</dt>
							<dd>{{ $t("privacy.recipients.sessionRecording") }}</dd>
							<dt>{{ $t("privacy.recipients.advertisingTitle") }}</dt>
							<dd>{{ $t("privacy.recipients.advertising") }}</dd>
						</dl>
					</div>
				</AccordionItem>
			</AccordionRoot>
			<p class="note">
				{{ $t("privacy.changeNotice") }}
				<a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer">{{ $t("privacy.policy") }}</a>
			</p>
			<p v-if="privacy.storageError" class="error" role="alert">{{ $t("privacy.storageError") }}</p>
			<div class="actions">
				<Button variant="secondary" size="s" @click="privacy.saveChoices(createPrivacyChoices())">
					{{ $t("privacy.decline") }}
				</Button>
				<Button variant="secondary" size="s" @click="privacy.saveChoices(createPrivacyChoices(true))">
					{{ $t("privacy.acceptAll") }}
				</Button>
				<Button type="submit" variant="primary" size="s">{{ $t("privacy.save") }}</Button>
			</div>
		</form>
	</dialog>
</template>

<style scoped>
.privacy-banner,
.privacy-dialog {
	box-sizing: border-box;
	color: var(--neutral-800);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	box-shadow: var(--shadow-xl);

	h3 {
		margin: 0;
		color: var(--neutral-900);
		font-weight: var(--font-weight-semibold);
	}

	p {
		margin: 0;
		font-size: var(--font-size-small);
		line-height: var(--line-height-relaxed);
		text-wrap: pretty;
	}

	a {
		color: var(--primary-green);
		text-decoration: underline;
	}

	.error {
		color: var(--accent-warning);
	}
}

.privacy-banner {
	position: fixed;
	z-index: 10000;
	left: 50%;
	bottom: var(--spacing-md);
	transform: translateX(-50%);
	width: min(620px, calc(100vw - 2 * var(--spacing-md)));
	max-height: calc(100dvh - 2 * var(--spacing-md));
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-md);

	h3 {
		font-size: var(--font-size-small);
	}

	.actions {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding-top: var(--spacing-xs);
	}

	.links,
	.choices {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
	}

	.links {
		flex: 1;
	}

	.policy {
		margin-right: auto;
		font-size: var(--font-size-xs);
	}

	.settings {
		color: var(--neutral-600);
	}
}

.privacy-dialog {
	margin: auto;
	width: min(600px, calc(100vw - 2 * var(--spacing-md)));
	max-height: calc(100dvh - 2 * var(--spacing-md));
	padding: var(--spacing-lg);

	&::backdrop {
		background-color: var(--overlay-backdrop);
	}

	.heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-md);
		margin-bottom: var(--spacing-md);

		h3 {
			font-size: var(--font-size-base);
			line-height: 1;
		}

		.close {
			margin-top: calc(var(--space-4) * -1);
		}
	}

	form {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-smd);
		margin-top: var(--spacing-md);
	}

	.purposes {
		--width-toggle: var(--space-32);
		--height-toggle: var(--space-16);

		margin: 0;
		padding: 0;
		list-style: none;
		border: var(--border-width-thin) solid var(--neutral-100);
		border-radius: var(--border-radius-md);
		background: var(--neutral-50);

		li {
			display: flex;
			align-items: flex-start;
			gap: var(--spacing-md);
			padding: var(--spacing-smd) var(--spacing-md);

			& + li {
				border-top: var(--border-width-thin) solid var(--neutral-100);
			}
		}

		.copy {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);
		}

		.name {
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
		}

		p {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}
	}

	.recipients {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding-inline-start: calc(var(--size-icon-lg) + var(--spacing-xs));
		font-size: var(--font-size-xs);
		color: var(--neutral-600);

		p {
			font-size: inherit;
		}

		dl {
			margin: 0;
		}

		dt {
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
		}

		dd {
			margin: 0 0 var(--spacing-xs);
		}
	}

	.note {
		font-size: var(--font-size-xs);
		color: var(--neutral-600);
	}

	.actions {
		display: flex;
		justify-content: flex-end;
		gap: var(--spacing-sm);
		padding-top: var(--spacing-sm);
	}
}

:global(body:has(.mobile-nav) .privacy-banner) {
	bottom: calc(var(--space-56) + env(safe-area-inset-bottom) + var(--spacing-sm));
}

@media (max-width: 640px) {
	.privacy-banner {
		.actions {
			flex-direction: column;
			align-items: stretch;
			gap: var(--spacing-smd);
		}

		.links {
			order: 1;
			flex-direction: row-reverse;
		}

		.policy {
			margin-right: 0;
		}

		.links > *,
		.choices > * {
			flex: 1;
			text-align: center;
		}
	}

	.privacy-dialog .actions {
		flex-wrap: wrap;

		> * {
			flex: 1;
		}
	}
}
</style>
